import type { Profile } from './profiles';

/**
 * Designer profile with specific design projects
 */
export const designerProfile: Profile = {
  name: 'Designer',
  minMeetingsPerDay: 1,
  averageSwitchingFrequencySeconds: 600, // Switch context every ~10 minutes on average
  meetings: [
    {
      title: 'Design Team Weekly Sync',
      durationMinutes: 60,
      entries: [
        { tool: 'figma', title: 'Weekly Design Review Board - Q3W2' },
        { tool: 'notion', title: 'Design Team Weekly Notes - June 2024' },
        { tool: 'linear', title: 'Design Team Tasks - Current Sprint' },
      ],
      aiNotes: {
        concise: 'Reviewing design work and planning sprint tasks.',
        detailed:
          'Participating in weekly design team sync meeting, reviewing design work in Figma, documenting discussion points, and organizing current sprint tasks in Linear.',
      },
    },
    {
      title: 'TT-4024: Mobile App Design Review',
      durationMinutes: 90,
      entries: [
        { tool: 'figma', title: 'Mobile app - Dashboard Redesign v3' },
        { tool: 'figma', title: 'Mobile app - Navigation Flow' },
        { tool: 'notion', title: 'Mobile App Review Notes - June 17' },
        { tool: 'linear', title: 'TT-4024: Mobile app navigation redesign' },
      ],
      aiNotes: {
        concise: 'Evaluating mobile app dashboard and navigation designs.',
        detailed:
          'Reviewing mobile app dashboard redesign and navigation flow in Figma, taking detailed notes on feedback, and updating the navigation redesign task in Linear.',
      },
    },
    {
      title: 'User Testing Session: Dashboard v3',
      durationMinutes: 120,
      entries: [
        { tool: 'figma', title: 'Analytics Dashboard - User Testing Prototype' },
        { tool: 'notion', title: 'User Testing Script - Dashboard v3' },
        { tool: 'notion', title: 'User Testing Live Notes' },
      ],
      aiNotes: {
        concise: 'Conducting user testing for dashboard prototype.',
        detailed:
          'Facilitating user testing session for the analytics dashboard prototype, following test script, and documenting user feedback and observations in real-time notes.',
      },
    },
    {
      title: '1:1 with Alex (Design Director)',
      durationMinutes: 30,
      entries: [
        { tool: 'notion', title: 'Career Growth Plan - 2024' },
        { tool: 'figma', title: 'Portfolio Update - June 2024' },
        { tool: 'slack', title: 'Alex (DM)' },
      ],
      aiNotes: {
        concise: 'Discussing career growth and portfolio development.',
        detailed:
          'Meeting with Design Director to review career growth plan, presenting updated portfolio work in Figma, and exchanging feedback via direct messages.',
      },
    },
    {
      title: 'TT-4127: Dashboard Redesign Planning',
      durationMinutes: 60,
      entries: [
        { tool: 'figma', title: 'Analytics Dashboard - Data Visualization' },
        { tool: 'notion', title: 'Dashboard Redesign Project Brief' },
        { tool: 'linear', title: 'TT-4127: Improve dashboard data visualization' },
      ],
      aiNotes: {
        concise: 'Planning dashboard data visualization improvements.',
        detailed:
          'Planning analytics dashboard redesign with focus on data visualization, reviewing project brief documentation, and updating task requirements in Linear.',
      },
    },
    {
      title: 'TT-3928: Q3 Brand Refresh Presentation',
      durationMinutes: 45,
      entries: [
        { tool: 'figma', title: 'Brand refresh 2025 - Color System' },
        { tool: 'figma', title: 'Brand refresh 2025 - Presentation Deck' },
        { tool: 'notion', title: 'Brand Refresh Implementation Plan' },
      ],
      aiNotes: {
        concise: 'Presenting brand refresh concept and implementation plan.',
        detailed:
          'Presenting brand refresh concept featuring updated color system in Figma, walking through presentation deck, and discussing the implementation plan timeline with stakeholders.',
      },
    },
    {
      title: 'WCAG 2.2 Accessibility Workshop',
      durationMinutes: 90,
      entries: [
        { tool: 'figma', title: 'Accessibility Audit - Current Components' },
        { tool: 'notion', title: 'WCAG 2.2 Guidelines & Requirements' },
        { tool: 'linear', title: 'Accessibility Improvement Tasks' },
      ],
      aiNotes: {
        concise: 'Learning and applying WCAG accessibility guidelines to designs.',
        detailed:
          'Participating in accessibility workshop focused on WCAG 2.2 compliance, conducting audit of current design components, reviewing guidelines, and planning improvement tasks.',
      },
    },
    {
      title: 'Design System Working Group',
      durationMinutes: 60,
      entries: [
        { tool: 'figma', title: 'Design System - Component Audit' },
        { tool: 'notion', title: 'Design System: Component Updates Q3' },
        { tool: 'linear', title: 'TT-4009: Update form controls in design system' },
      ],
      aiNotes: {
        concise: 'Collaborating on design system component updates.',
        detailed:
          'Working with design system group to audit components, planning Q3 component updates, and prioritizing form control improvements in the design system.',
      },
    },
    {
      title: 'TT-4098: User Research Findings Review',
      durationMinutes: 45,
      entries: [
        { tool: 'notion', title: 'User Research Synthesis: Dashboard Features' },
        { tool: 'figma', title: 'User Journey Maps - Enterprise Plan' },
        { tool: 'linear', title: 'TT-4098: Incorporate user research findings' },
      ],
      aiNotes: {
        concise: 'Analyzing user research data and planning design improvements.',
        detailed:
          'Reviewing synthesized user research findings for dashboard features, examining user journey maps for enterprise customers, and planning implementation of insights into design work.',
      },
    },
    {
      title: 'Design & Engineering Pairing Session',
      durationMinutes: 60,
      entries: [
        { tool: 'figma', title: 'Component Implementation Details' },
        { tool: 'github', title: 'UI Component Repository' },
        { tool: 'slack', title: 'Rachel (DM)' },
      ],
      aiNotes: {
        concise: 'Collaborating with engineering on component implementation.',
        detailed:
          'Pairing with engineering team to review component implementation details in Figma, examining UI component code repository, and communicating directly with the engineering lead.',
      },
    },
  ],
  sessions: [
    {
      name: 'Mobile App Redesign',
      predominantTool: 'figma',
      entries: [
        { tool: 'figma', title: 'Mobile app - Component Library' },
        { tool: 'figma', title: 'Mobile app - Dashboard Redesign v3' },
        { tool: 'figma', title: 'Mobile app - Navigation Flow' },
        { tool: 'figma', title: 'Mobile app - Onboarding Screens' },
        { tool: 'figma', title: 'Mobile app - Dark Mode Adaptation' },
        { tool: 'figma', title: 'Mobile app - User Testing Prototypes' },
        { tool: 'figma', title: 'Mobile app - Design System Implementation' },
        { tool: 'linear', title: 'TT-4024: Mobile app navigation redesign' },
        { tool: 'slack', title: 'mobile-app-redesign' },
        { tool: 'loom', title: 'Mobile Navigation Flow Demo' },
        { url: 'https://behance.net', title: 'Behance - Mobile app navigation patterns' },
        { url: 'https://google.com', title: 'Google search: Mobile app onboarding best practices' },
        { url: 'https://mobbin.com', title: 'Mobbin - Mobile UI design patterns' },
        { url: 'https://apple.com/design', title: 'Apple Human Interface Guidelines' },
        { url: 'https://material.io/design', title: 'Google Material Design - Mobile' },
      ],
      aiNotes: {
        concise: 'Designing comprehensive mobile app interface and user flows.',
        detailed:
          'Creating mobile app designs in Figma including component library, dashboard redesign, navigation flows, onboarding screens, dark mode, and interactive prototypes. Referencing design guidelines and researching navigation patterns.',
      },
    },
    {
      name: 'Brand Refresh',
      predominantTool: 'figma',
      entries: [
        { tool: 'figma', title: 'Brand 2024 - Color System' },
        { tool: 'figma', title: 'Brand 2024 - Typography' },
        { tool: 'figma', title: 'Brand 2024 - Logo Variations' },
        { tool: 'figma', title: 'Brand 2024 - Marketing Assets' },
        { tool: 'figma', title: 'Brand 2024 - Social Media Templates' },
        { tool: 'notion', title: 'Brand Guidelines Documentation' },
        { tool: 'linear', title: 'TT-3928: Brand refresh for Q3 marketing campaign' },
        { url: 'https://coolors.co', title: 'Coolors - Brand palette explorations' },
        { url: 'https://fonts.google.com', title: 'Google Fonts - Sans serif options for UI' },
        { url: 'https://unsplash.com', title: 'Unsplash - Team photography for marketing' },
      ],
      aiNotes: {
        concise: 'Updating brand identity with new colors, typography and assets.',
        detailed:
          'Developing comprehensive brand refresh design system in Figma, including color palette, typography, logo variations, marketing assets, and social media templates. Documenting brand guidelines and researching font options.',
      },
    },
    {
      name: 'Dashboard UI Improvements',
      predominantTool: 'figma',
      entries: [
        { tool: 'figma', title: 'Analytics Dashboard - Data Visualization' },
        { tool: 'figma', title: 'Analytics Dashboard - User Flow' },
        { tool: 'figma', title: 'Analytics Dashboard - Responsive Layouts' },
        { tool: 'figma', title: 'Analytics Dashboard - Interactive Prototype' },
        { tool: 'notion', title: 'User Research: Dashboard Usability Findings' },
        { tool: 'linear', title: 'TT-4127: Improve dashboard data visualization' },
        { tool: 'loom', title: 'Dashboard UI review for dev team' },
        { url: 'https://dribbble.com', title: 'Dribbble - Analytics dashboard inspiration' },
        { url: 'https://nngroup.com', title: 'Nielsen Norman Group - Dashboard design guidelines' },
      ],
      aiNotes: {
        concise: 'Enhancing analytics dashboard with improved visualizations and flows.',
        detailed:
          'Redesigning analytics dashboard with focus on data visualization, user flows, responsive layouts, and interactive prototypes. Incorporating usability research findings and creating video review for development team.',
      },
    },
    {
      name: 'Design System Maintenance',
      predominantTool: 'figma',
      entries: [
        { tool: 'figma', title: 'Design System - Component Audit' },
        { tool: 'figma', title: 'Design System - Form Controls Update' },
        { tool: 'figma', title: 'Design System - Documentation' },
        { tool: 'figma', title: 'Design System - Accessibility Improvements' },
        { tool: 'notion', title: 'Design System: Component Usage Guidelines' },
        { tool: 'linear', title: 'TT-4009: Update form controls in design system' },
        { url: 'https://medium.com', title: 'Medium - Design systems at scale' },
        { url: 'https://material.io', title: 'Material Design - Form components reference' },
        {
          url: 'https://contrast-ratio.com',
          title: 'Contrast Ratio Checker - Accessibility testing',
        },
      ],
      aiNotes: {
        concise: 'Maintaining and improving the design system components.',
        detailed:
          'Auditing design system components, updating form controls, improving documentation, and enhancing accessibility. Researching design systems at scale and testing color contrast for accessibility compliance.',
      },
    },
    {
      name: 'Team Communication',
      predominantTool: 'slack',
      entries: [
        { tool: 'slack', title: 'design-team' },
        { tool: 'slack', title: 'product' },
        { tool: 'slack', title: 'Alex (DM)' },
        { tool: 'slack', title: 'Rachel (DM)' },
      ],
      aiNotes: {
        concise: 'Coordinating with design and product teams.',
        detailed:
          'Communicating with design team members and product team through Slack channels and direct messages with team leads.',
      },
    },
    {
      name: 'UI Animation Research',
      predominantTool: 'google_chrome',
      entries: [
        {
          url: 'https://smashingmagazine.com',
          title: 'Smashing Magazine - UI animation principles',
        },
        { url: 'https://youtube.com', title: 'YouTube - Advanced Figma prototyping techniques' },
        { url: 'https://dribbble.com', title: 'Dribbble - Animation showcase' },
        { tool: 'figma', title: 'Mobile - Animation Prototypes' },
        { tool: 'notion', title: 'Animation Principles: Research Notes' },
      ],
      aiNotes: {
        concise: 'Researching animation principles and techniques for interfaces.',
        detailed:
          'Studying UI animation principles and advanced Figma prototyping techniques, creating animation prototypes, and documenting research findings on animation principles.',
      },
    },
    {
      name: 'User Research',
      predominantTool: 'notion',
      entries: [
        { tool: 'notion', title: 'User Interview Notes: Mobile Usability' },
        { tool: 'notion', title: 'Research Synthesis: Dashboard Features' },
        { tool: 'notion', title: 'User Persona Updates: Enterprise Users' },
        { tool: 'figma', title: 'User Journey Maps - Enterprise Plan' },
        { tool: 'loom', title: 'User Research Summary for stakeholders' },
        { tool: 'linear', title: 'TT-4098: Incorporate user research findings' },
      ],
      aiNotes: {
        concise: 'Analyzing user interview data and creating journey maps.',
        detailed:
          'Synthesizing user interview notes for mobile usability, documenting research findings for dashboard features, updating user personas, creating user journey maps, and sharing research summary with stakeholders.',
      },
    },
    {
      name: 'Competitor UX Analysis',
      predominantTool: 'figma',
      entries: [
        { tool: 'figma', title: 'Competitor Analysis - UX Patterns' },
        { tool: 'notion', title: 'UX Competitive Analysis - Q3 2024' },
        { url: 'https://uxarchive.com', title: 'UX Archive - Mobile patterns' },
        { url: 'https://mobbin.com', title: 'Mobbin - Competitor UI flows' },
      ],
      aiNotes: {
        concise: 'Analyzing competitor user experience patterns.',
        detailed:
          'Conducting competitive analysis of UX patterns in Figma, documenting findings, and researching competitor UI flows and mobile patterns for benchmarking.',
      },
    },
    {
      name: 'Accessibility Testing',
      predominantTool: 'figma',
      entries: [
        { tool: 'figma', title: 'Accessibility Audit - Current Design System' },
        { tool: 'figma', title: 'Color Contrast Improvements' },
        { tool: 'notion', title: 'Accessibility Compliance Documentation' },
        { url: 'https://webaim.org', title: 'WebAIM - Contrast checker' },
        { url: 'https://a11yproject.com', title: 'A11y Project - Guidelines' },
      ],
      aiNotes: {
        concise: 'Testing and improving design system accessibility.',
        detailed:
          'Conducting accessibility audit of design system components, implementing color contrast improvements, documenting accessibility compliance, and researching best practices for accessible design.',
      },
    },
    {
      name: 'User Interview Preparation',
      predominantTool: 'notion',
      entries: [
        { tool: 'notion', title: 'User Interview Script - Mobile Navigation' },
        { tool: 'figma', title: 'User Testing Prototypes - Navigation Variations' },
        { tool: 'slack', title: 'user-research-team' },
        { url: 'https://nngroup.com', title: 'Nielsen Norman Group - Interview techniques' },
      ],
      aiNotes: {
        concise: 'Preparing user interview scripts and test materials.',
        detailed:
          'Developing user interview script for mobile navigation testing, creating interactive prototypes for testing different navigation variations, and researching effective interview techniques.',
      },
    },
    {
      name: 'Design System Documentation',
      predominantTool: 'figma',
      entries: [
        { tool: 'figma', title: 'Design System - Component Documentation' },
        { tool: 'figma', title: 'Design System - Usage Guidelines' },
        { tool: 'notion', title: 'Design System - Implementation Guide for Devs' },
        { tool: 'linear', title: 'TT-4087: Update design system documentation' },
      ],
      aiNotes: {
        concise: 'Creating comprehensive documentation for design system.',
        detailed:
          'Developing detailed documentation for design system components and usage guidelines in Figma, creating implementation guides for developers, and tracking documentation tasks in Linear.',
      },
    },
    {
      name: 'Interaction Design',
      predominantTool: 'figma',
      entries: [
        { tool: 'figma', title: 'Interactive Dashboard - Prototype v2' },
        { tool: 'figma', title: 'Microinteractions - Component States' },
        { tool: 'figma', title: 'Interactive Prototype - User Flows' },
        { url: 'https://principleformac.com', title: 'Principle - Animation reference' },
      ],
      aiNotes: {
        concise: 'Creating interactive prototypes and microinteractions.',
        detailed:
          'Building interactive dashboard prototypes, designing component state microinteractions, creating user flow prototypes, and researching animation reference materials.',
      },
    },
    {
      name: 'Illustration Work',
      predominantTool: 'adobe_illustrator',
      entries: [
        { tool: 'adobe_illustrator', title: 'Brand Illustrations - Q3 Campaign' },
        { tool: 'adobe_illustrator', title: 'Feature Icons - Dashboard Update' },
        { tool: 'figma', title: 'Illustration Integration - Marketing Site' },
        { url: 'https://dribbble.com', title: 'Dribbble - Illustration inspiration' },
      ],
      aiNotes: {
        concise: 'Creating brand illustrations and icons for marketing.',
        detailed:
          'Designing brand illustrations for marketing campaign and feature icons for dashboard update in Adobe Illustrator, integrating illustrations into the marketing site, and finding inspiration from Dribbble.',
      },
    },
    {
      name: 'Design Exploration',
      predominantTool: 'figma',
      entries: [
        { tool: 'figma', title: 'Exploration - Dashboard Alternative Layouts' },
        { tool: 'figma', title: 'Exploration - Typography System Refresh' },
        { tool: 'figma', title: 'Exploration - Dark Theme Variations' },
        { url: 'https://pinterest.com', title: 'Pinterest - Dashboard inspiration board' },
      ],
      aiNotes: {
        concise: 'Exploring alternative design directions and concepts.',
        detailed:
          'Creating exploratory designs for dashboard layouts, typography system refresh, and dark theme variations. Gathering inspiration from design resources to inform new directions.',
      },
    },
    {
      name: 'User Flow Mapping',
      predominantTool: 'figma',
      entries: [
        { tool: 'figma', title: 'User Flow - Onboarding Process v2' },
        { tool: 'figma', title: 'User Flow - Authentication Scenarios' },
        { tool: 'notion', title: 'User Flow Documentation - Onboarding' },
        { url: 'https://flowmapp.com', title: 'FlowMapp - User flow techniques' },
      ],
      aiNotes: {
        concise: 'Mapping user flows for onboarding and authentication.',
        detailed:
          'Creating user flow diagrams for onboarding process and authentication scenarios, documenting flow details, and researching best practices for user flow design.',
      },
    },
    {
      name: 'Email Template Design',
      predominantTool: 'figma',
      entries: [
        { tool: 'figma', title: 'Email Templates - Welcome Series' },
        { tool: 'figma', title: 'Email Templates - Newsletter Refresh' },
        { tool: 'figma', title: 'Email Templates - Transactional Messages' },
        { url: 'https://reallygoodemails.com', title: 'Really Good Emails - Inspiration' },
      ],
      aiNotes: {
        concise: 'Designing email templates for various communication types.',
        detailed:
          'Creating email template designs for welcome series, newsletter refresh, and transactional messages, researching email design inspiration and best practices.',
      },
    },
    {
      name: 'Marketing Design Support',
      predominantTool: 'figma',
      entries: [
        { tool: 'figma', title: 'Marketing Site - Homepage Refresh' },
        { tool: 'figma', title: 'Landing Page - Q3 Campaign' },
        { tool: 'figma', title: 'Social Media Assets - Product Launch' },
        { tool: 'slack', title: 'marketing-design' },
      ],
      aiNotes: {
        concise: 'Creating marketing site designs and campaign assets.',
        detailed:
          'Designing marketing site homepage refresh, landing page for Q3 campaign, and social media assets for product launch. Collaborating with marketing team on design requirements.',
      },
    },
    {
      name: 'Design Workshop Preparation',
      predominantTool: 'figma',
      entries: [
        { tool: 'figma', title: 'Workshop - Design Sprint Materials' },
        { tool: 'notion', title: 'Design Workshop - Facilitation Guide' },
        { tool: 'miro', title: 'Design Sprint - Workshop Templates' },
        { url: 'https://designsprintkit.withgoogle.com', title: 'Google Design Sprint Kit' },
      ],
      aiNotes: {
        concise: 'Preparing materials for design sprint workshop.',
        detailed:
          'Creating design sprint workshop materials in Figma, developing facilitation guide, preparing workshop templates in Miro, and researching design sprint methodologies.',
      },
    },
  ],
};
